/** Global notification store: all errors AND successes are surfaced the same way (a stack of
 * dismissible messages at the top of the screen), regardless of their source (mutation errors,
 * chat stream errors, form submissions…). */
export type NotificationVariant = "error" | "success" | "information";

export interface Notification {
  id: number;
  variant: NotificationVariant;
  message: string;
}

type Listener = (notifications: Notification[]) => void;

const listeners = new Set<Listener>();
let notifications: Notification[] = [];
let nextId = 1;

function emit() {
  for (const listener of listeners) listener(notifications);
}

/** Push a notification onto the global stack. Returns its id. */
export function emitNotification(
  variant: NotificationVariant,
  message: string,
): number {
  const id = nextId++;
  notifications = [...notifications, { id, variant, message }];
  emit();
  return id;
}

export function dismissNotification(id: number): void {
  notifications = notifications.filter((item) => item.id !== id);
  emit();
}

export function subscribeNotifications(listener: Listener): () => void {
  listeners.add(listener);
  listener(notifications);
  return () => {
    listeners.delete(listener);
  };
}

/** Convenience shortcuts. `emitApiError` is kept for the QueryClient mutation cache. */
export function emitApiError(message: string): void {
  emitNotification("error", message);
}

export function notifySuccess(message: string): void {
  emitNotification("success", message);
}
